import {
  CalendarDaysIcon,
  WrenchScrewdriverIcon,
  TagIcon,
  XCircleIcon,
  UsersIcon,
  IdentificationIcon,
  CreditCardIcon,
  BanknotesIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  ShoppingBagIcon,
  AcademicCapIcon,
  TrophyIcon,
  ChartBarIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  PaintBrushIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  SparklesIcon,
  BuildingOffice2Icon,
  BookOpenIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  ClockIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  QueueListIcon,
} from '@heroicons/react/24/outline';
import { hasRequiredDocuments } from '../utils/staffDocuments';

// Every page a staff member can reach from a tile. The Admin page shows them
// by section; the home screen shows the ones each person picks as favourites.
// `homeOnly` tiles don't appear on the Admin page; `area` and `level` are the
// access the tile needs (utils/access.js); `anyOf` ([[area, level], ...])
// lets in anyone with one of them, as AccessRoute's anyOf does; `clubAdmin` needs a Club admin;
// `feature` needs that feature on for the club (a list: all of them). Tiles
// with neither are for every member of staff.
export const STAFF_TILES = [
  { id: 'admin', label: 'Admin', to: '/admin-hub', icon: Cog6ToothIcon, homeOnly: true },
  { id: 'shop', label: 'Shop', to: '/booking/shop', icon: ShoppingBagIcon, homeOnly: true, feature: 'shop' },

  { id: 'sessions', section: 'Sessions', label: 'Sessions', to: '/booking/admin', icon: CalendarDaysIcon, area: 'sessions', level: 'VIEW' },
  { id: 'register', section: 'Sessions', label: 'Register', to: '/booking/admin/register', icon: ClipboardDocumentCheckIcon, area: 'registers', level: 'VIEW' },
  { id: 'closures', section: 'Sessions', label: 'Closures', to: '/booking/admin/closures', icon: XCircleIcon, area: 'sessionSetup', level: 'MANAGE' },

  { id: 'competitions', section: 'Competitions', label: 'Competitions', to: '/booking/admin/competitions', icon: TrophyIcon, feature: 'competitions', area: 'competitions', level: 'VIEW' },
  { id: 'requirements', section: 'Competitions', label: 'Requirements', to: '/booking/requirements', icon: BookOpenIcon, feature: 'requirements', area: 'competitions', level: 'VIEW' },

  { id: 'gymnasts', section: 'Skill Tracking', label: 'Gymnasts', to: '/gymnasts', icon: ArrowTrendingUpIcon, area: 'skillTracking', level: 'VIEW' },
  { id: 'routines-to-check', section: 'Skill Tracking', label: 'Routines to Check', to: '/routines-to-check', icon: ClipboardDocumentCheckIcon, badge: 'routinesToCheck', feature: 'gymnastRoutines', area: 'skillTracking', level: 'VIEW' },
  { id: 'diaries', section: 'Skill Tracking', label: 'Training Diaries', to: '/diary-overview', icon: BookOpenIcon, feature: 'diary', area: 'skillTracking', level: 'VIEW' },
  { id: 'certificates', section: 'Skill Tracking', label: 'Certificates', to: '/certificates', icon: AcademicCapIcon, feature: 'certificates', area: 'skillSetup', level: 'MANAGE' },
  { id: 'levels', section: 'Skill Tracking', label: 'Levels', to: '/levels', icon: ChartBarIcon, area: 'skillSetup', level: 'MANAGE' },
  { id: 'skills', section: 'Skill Tracking', label: 'All Skills', to: '/skills', icon: ChartBarIcon, area: 'skillSetup', level: 'MANAGE' },

  { id: 'first-aid', section: 'Safety', label: 'First Aid Reports', to: '/booking/admin/incidents', icon: ExclamationTriangleIcon, className: 'dashboard-tile--incidents', area: 'firstAid', level: 'VIEW' },
  { id: 'welfare', section: 'Safety', label: 'Welfare Reports', to: '/booking/admin/welfare', icon: ShieldExclamationIcon, className: 'dashboard-tile--welfare', area: 'welfare', level: 'VIEW' },

  { id: 'members', section: 'Members', label: 'Members', to: '/booking/admin/members', icon: UsersIcon, badge: 'approvalsWaiting', area: 'members', level: 'VIEW' },
  { id: 'bg-numbers', section: 'Members', label: 'BG Numbers', to: '/booking/admin/bg-numbers', icon: IdentificationIcon, badge: 'bgToCheck', feature: 'bgNumbers', area: 'members', level: 'VIEW' },

  { id: 'memberships', section: 'Payments', label: 'Memberships', to: '/booking/admin/memberships', icon: BanknotesIcon, badge: 'paymentsBehind', feature: 'memberships', area: 'payments', level: 'VIEW' },
  { id: 'credits', section: 'Payments', label: 'Credits', to: '/booking/admin/credits', icon: BanknotesIcon, area: 'payments', level: 'VIEW' },
  { id: 'charges', section: 'Payments', label: 'Charges', to: '/booking/admin/charges', icon: CreditCardIcon, area: 'payments', level: 'VIEW' },
  { id: 'payments', section: 'Payments', label: 'Payments', to: '/booking/admin/payments', icon: BanknotesIcon, area: 'payments', level: 'VIEW' },
  { id: 'payouts', section: 'Payments', label: 'Card payments', to: '/booking/admin/card-payments', icon: BanknotesIcon, area: 'payments', level: 'VIEW' },
  { id: 'member-payments', section: 'Payments', label: 'Member Payments', to: '/booking/admin/member-payments', icon: CreditCardIcon, area: 'payments', level: 'VIEW' },

  { id: 'post-notice', section: 'Communications', label: 'Post a notice', to: '/booking/noticeboard?new=1', icon: MegaphoneIcon, area: 'communications', level: 'MANAGE' },
  // In-app messaging; Reports in it are for welfare. Coaches without Communications use Account > Messages
  { id: 'chat', section: 'Communications', label: 'Messages', to: '/booking/admin/chat', icon: ChatBubbleLeftRightIcon, badge: 'chatUnread', feature: 'messaging', anyOf: [['communications', 'VIEW'], ['welfare', 'MANAGE']] },
  // Members' own kit; reported listings are for Communications: Manage, and
  // welfare can open the chats about a listing. Its chats need Messaging on.
  { id: 'community', section: 'Communications', label: 'Community shop', to: '/booking/admin/community', icon: ShoppingBagIcon, badge: 'communityReports', feature: ['messaging', 'communityShop'], anyOf: [['communications', 'VIEW'], ['welfare', 'VIEW']] },
  // Bulk email to members (id kept so saved tile choices still find it)
  { id: 'messages', section: 'Communications', label: 'Email members', to: '/booking/admin/messages', icon: EnvelopeIcon, area: 'communications', level: 'MANAGE' },
  { id: 'shop-products', section: 'Communications', label: 'Shop Products', to: '/booking/admin/shop-products', icon: ShoppingBagIcon, feature: 'shop', area: 'shop', level: 'MANAGE' },
  { id: 'shop-orders', section: 'Communications', label: 'Shop Orders', to: '/booking/admin/shop-orders', icon: ShoppingBagIcon, badge: 'pendingOrders', feature: 'shop', area: 'shop', level: 'VIEW' },

  { id: 'invoices', section: 'Business', label: 'Invoices', to: '/booking/admin/invoices', icon: DocumentTextIcon, feature: 'invoices', area: 'business', level: 'VIEW' },
  { id: 'business-contacts', section: 'Business', label: 'Business Contacts', to: '/booking/admin/business-contacts', icon: BuildingOffice2Icon, feature: 'invoices', area: 'business', level: 'VIEW' },

  { id: 'staff-documents', section: 'Staff', label: 'Staff documents', to: '/staff-documents', icon: DocumentTextIcon, staffDocuments: true, feature: 'codeOfConduct' },
  { id: 'coach-hours', section: 'Staff', label: 'Coach Hours', to: '/booking/admin/coach-hours', icon: ClockIcon, feature: 'sessionCoaches', area: 'sessionSetup', level: 'MANAGE' },
  { id: 'roles', section: 'Staff', label: 'Roles', to: '/booking/admin/roles', icon: UserGroupIcon, clubAdmin: true },

  { id: 'club-settings', section: 'Club Settings', label: 'Club Settings', to: '/club-settings', icon: Cog6ToothIcon, area: 'settings', level: 'MANAGE' },
  { id: 'branding', section: 'Club Settings', label: 'Club Branding', to: '/branding', icon: PaintBrushIcon, area: 'settings', level: 'MANAGE' },
  { id: 'session-management', section: 'Club Settings', label: 'Session Management', to: '/booking/admin/session-management', icon: WrenchScrewdriverIcon, area: 'sessionSetup', level: 'MANAGE' },
  { id: 'session-types', section: 'Club Settings', label: 'Session Types', to: '/booking/admin/session-types', icon: TagIcon, area: 'sessionSetup', level: 'MANAGE' },
  { id: 'certificate-setup', section: 'Club Settings', label: 'Certificate Setup', to: '/certificate-designer', icon: DocumentTextIcon, feature: 'certificates', area: 'skillSetup', level: 'MANAGE' },
  { id: 'recipient-groups', section: 'Club Settings', label: 'Recipient Groups', to: '/booking/admin/recipient-groups', icon: QueueListIcon, anyOf: [['communications', 'VIEW'], ['members', 'MANAGE']] },
  { id: 'help', section: 'Club Settings', label: 'Help Page', to: '/booking/admin/help', icon: QuestionMarkCircleIcon, area: 'settings', level: 'MANAGE' },

  { id: 'audit-log', section: 'Logs', label: 'Audit Log', to: '/booking/admin/audit-log', icon: ClipboardDocumentListIcon, area: 'auditLog', level: 'VIEW' },
  { id: 'changelog', section: 'Logs', label: 'Recent Updates', to: '/booking/admin/changelog', icon: SparklesIcon, feature: 'recentUpdates', clubAdmin: true },
];

// The Admin page's sections in this order until someone arranges their own
export const DEFAULT_SECTION_ORDER = ['Sessions', 'Members', 'Payments', 'Skill Tracking', 'Competitions', 'Communications', 'Business', 'Safety', 'Staff', 'Club Settings', 'Logs'];

// Section names in someone's saved order. Sections they haven't placed (new
// ones, or ones they couldn't see when they saved) keep their default spot.
export function orderSections(names, saved = []) {
  const rank = name => {
    const def = DEFAULT_SECTION_ORDER.indexOf(name);
    return def === -1 ? DEFAULT_SECTION_ORDER.length : def;
  };
  const placed = (saved || []).filter(n => names.includes(n));
  const rest = names.filter(n => !placed.includes(n)).sort((a, b) => rank(a) - rank(b));
  // Slot each unplaced section in just after the last one that comes before it by default
  const result = [...placed];
  rest.forEach(name => {
    let after = -1;
    result.forEach((n, i) => { if (rank(n) < rank(name)) after = i; });
    result.splice(after + 1, 0, name);
  });
  return result;
}

export const MAX_HOME_TILES = 6;

// Until someone picks their own, the home screen has just the Admin tile
const DEFAULT_HOME_TILES = ['admin'];

// Staff documents only show to staff with staff documents access until the club requires one of them
export const staffDocumentsOn = hasRequiredDocuments;

// `can` is useAuth().can (or canFor(access) from utils/access.js)
export const tilesFor = ({ can = () => false, isClubAdmin, staffDocumentsRequired, features }) =>
  STAFF_TILES.filter(t => (!t.area || can(t.area, t.level))
    && (!t.anyOf || t.anyOf.some(([area, level]) => can(area, level)))
    && (!t.clubAdmin || isClubAdmin)
    && (!t.staffDocuments || can('staffDocuments') || staffDocumentsRequired)
    && [].concat(t.feature || []).every(f => features?.[f] !== false));

// The tiles for someone's home screen, in the order they picked them
export function homeTilesFor(user, access) {
  const allowed = new Map(tilesFor(access).map(t => [t.id, t]));
  // 'code-of-conduct' was this tile's id before it became Staff documents
  const picked = (user?.homeTiles?.length ? user.homeTiles : DEFAULT_HOME_TILES).map(id => (id === 'code-of-conduct' ? 'staff-documents' : id));
  return picked.map(id => allowed.get(id)).filter(Boolean).slice(0, MAX_HOME_TILES);
}
